import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import api from "../utils/axios";
import { API_ROUTES } from "./routes";
import { showToast } from "../components/ui/toast";

/**
 * Ayrshare posting usage.
 *
 * One endpoint serves every level; which one you get depends on the params:
 *   {}                                   -> summary
 *   { level: "organization" }             -> organizations
 *   { organizationId }                    -> platforms in that org
 *   { organizationId, platform }          -> agents on that platform
 *   { organizationId, platform, userId }  -> that agent's post types
 *   { tree: true }                        -> the whole nested structure
 *
 * The Financial Summary uses tree mode. Every level is derived from the same
 * aggregation server-side, so fetching them one click at a time would repeat
 * that query (plus the org-resolution lookups) four times over. One call, then
 * expand locally.
 *
 * Filters ride along at any level: timeFrame | from/to, postType, status.
 * Agents holding an Ayrshare profile who posted nothing appear with
 * totalPosts 0, sorted last — a profile we pay for that nobody used.
 */
export const useGetAyrshareUsageQuery = (params = {}, enabled = true) => {
  const { data, isPending, isFetching, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_AYRSHARE_USAGE, params],
    queryFn: () => api.get(API_ROUTES.GET_AYRSHARE_USAGE, { params }),
    placeholderData: keepPreviousData,
    enabled,
  });
  return {
    data: data?.data?.data ?? null,
    isPending,
    isFetching,
    isError,
    error,
  };
};

/**
 * Filter values that actually exist in the data. Platform is a free-form
 * string set by Ayrshare account linking rather than an enum, so it has to
 * come from the server instead of being hardcoded.
 */
/**
 * Upserts the Ayrshare monthly subscription cost.
 * On success, invalidates the usage query so monthlyPrices + totalMonthlyCost
 * are re-fetched automatically.
 */
export const useSetAyrshareMonthlyPriceMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ month, amount }) =>
      api.put(API_ROUTES.SET_AYRSHARE_MONTHLY_PRICE, { month, amount }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [API_ROUTES.GET_AYRSHARE_USAGE] });
    },
    onError: () => showToast("error", "Failed to save monthly price"),
  });
};

export const useGetAyrshareFiltersQuery = () => {
  const { data, isPending, isError, error } = useQuery({
    queryKey: [API_ROUTES.GET_AYRSHARE_FILTERS],
    queryFn: () => api.get(API_ROUTES.GET_AYRSHARE_FILTERS),
    staleTime: 5 * 60 * 1000,
  });
  return { data: data?.data?.data ?? null, isPending, isError, error };
};
